class TootoAboutCollectors extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.gallery = this.querySelector('.tooto-about-collectors__track');
    this.viewport = this.querySelector('.tooto-about-collectors__viewport');
    this.cards = [...this.querySelectorAll('.tooto-about-collectors__card')];
    this.previousButton = this.querySelector('[data-tooto-about-collectors-prev]');
    this.nextButton = this.querySelector('[data-tooto-about-collectors-next]');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.autoplayEnabled = this.dataset.autoplay === 'true';
    this.autoplayDelay = Number(this.dataset.autoplayDelay) || 5000;
    this.isPointerInside = false;
    this.isInteracting = false;
    this.isGalleryReady = false;

    this.handlePrevious = () => {
      this.moveCarousel(-1);
      this.restartAutoplay();
    };
    this.handleNext = () => {
      this.moveCarousel(1);
      this.restartAutoplay();
    };
    this.handlePointerEnter = () => {
      this.isPointerInside = true;
      this.stopAutoplay();
    };
    this.handlePointerLeave = () => {
      this.isPointerInside = false;
      this.startAutoplay();
    };
    this.handlePointerDown = () => {
      this.isInteracting = true;
      this.stopAutoplay();
    };
    this.handlePointerUp = () => {
      this.isInteracting = false;
      this.startAutoplay();
    };
    this.handleFocusIn = () => this.stopAutoplay();
    this.handleFocusOut = (event) => {
      if (!this.contains(event.relatedTarget)) this.startAutoplay();
    };
    this.handleVisibilityChange = () => {
      if (document.hidden) this.stopAutoplay();
      else this.startAutoplay();
    };

    this.previousButton?.addEventListener('click', this.handlePrevious);
    this.nextButton?.addEventListener('click', this.handleNext);
    this.addEventListener('mouseenter', this.handlePointerEnter);
    this.addEventListener('mouseleave', this.handlePointerLeave);
    this.viewport?.addEventListener('pointerdown', this.handlePointerDown, { passive: true });
    this.viewport?.addEventListener('pointerup', this.handlePointerUp, { passive: true });
    this.viewport?.addEventListener('pointercancel', this.handlePointerUp, { passive: true });
    this.addEventListener('focusin', this.handleFocusIn);
    this.addEventListener('focusout', this.handleFocusOut);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    if ('ResizeObserver' in window && this.viewport) {
      this.carouselResizeObserver = new ResizeObserver(() => this.updateCarousel());
      this.carouselResizeObserver.observe(this.viewport);
    }

    requestAnimationFrame(() => this.updateCarousel());

    if (this.reducedMotion || !('IntersectionObserver' in window)) {
      this.classList.add('is-visible', 'is-gallery-visible');
      this.cards.forEach((card) => card.classList.add('is-revealed'));
      this.isGalleryReady = true;
      this.startAutoplay();
      return;
    }

    this.cardObserver = new IntersectionObserver(
      (entries) => {
        const enteringCards = entries
          .filter((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.45)
          .sort((first, second) => first.target.offsetLeft - second.target.offsetLeft);

        enteringCards.forEach((entry, index) => {
          const card = entry.target;
          if (card.classList.contains('is-revealed')) return;

          const curtainDelay = index * 90;
          card.style.setProperty('--tooto-about-collectors-curtain-delay', `${curtainDelay}ms`);
          card.classList.add('is-revealed');
          this.cardObserver?.unobserve(card);
        });
      },
      {
        root: null,
        rootMargin: '0px 0px -8% 0px',
        threshold: [0.45],
      }
    );

    this.cards.forEach((card) => this.cardObserver.observe(card));

    this.headingObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        this.classList.add('is-visible');
        this.headingObserver?.disconnect();
      },
      { threshold: 0.08 }
    );

    this.galleryObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.35) return;
        this.classList.add('is-gallery-visible');
        this.isGalleryReady = true;
        this.galleryObserver?.disconnect();
        this.startAutoplay();
      },
      { threshold: [0.35], rootMargin: '0px 0px -12% 0px' }
    );

    this.headingObserver.observe(this);
    this.galleryObserver.observe(this.gallery || this);
  }

  updateCarousel() {
    if (!this.viewport) return;

    this.hasCarouselOverflow = this.viewport.scrollWidth > this.viewport.clientWidth + 2;
    this.classList.toggle('is-carousel-static', !this.hasCarouselOverflow);
    this.previousButton?.toggleAttribute('hidden', !this.hasCarouselOverflow);
    this.nextButton?.toggleAttribute('hidden', !this.hasCarouselOverflow);

    if (this.hasCarouselOverflow) this.startAutoplay();
    else this.stopAutoplay();
  }

  getCarouselMetrics() {
    const firstCard = this.cards[0];
    if (!firstCard || !this.viewport) return { current: 0, maxStart: 0 };

    const step = firstCard.getBoundingClientRect().width;
    const visibleCount = Math.max(1, Math.floor((this.viewport.clientWidth + 1) / step));
    const maxStart = Math.max(0, this.cards.length - visibleCount);
    const current = this.cards.reduce((closestIndex, card, index) => {
      if (index > maxStart) return closestIndex;
      const currentDistance = Math.abs(this.cards[closestIndex].offsetLeft - this.viewport.scrollLeft);
      const nextDistance = Math.abs(card.offsetLeft - this.viewport.scrollLeft);
      return nextDistance < currentDistance ? index : closestIndex;
    }, 0);

    return { current, maxStart };
  }

  moveCarousel(direction) {
    if (!this.hasCarouselOverflow || !this.viewport || !this.cards.length) return;

    const { current, maxStart } = this.getCarouselMetrics();
    let target = current + direction;
    if (target > maxStart) target = 0;
    if (target < 0) target = maxStart;

    this.viewport.scrollTo({
      left: this.cards[target]?.offsetLeft || 0,
      behavior: this.reducedMotion ? 'auto' : 'smooth',
    });
  }

  startAutoplay() {
    if (
      !this.autoplayEnabled ||
      this.reducedMotion ||
      !this.isGalleryReady ||
      !this.hasCarouselOverflow ||
      this.isPointerInside ||
      this.isInteracting ||
      this.matches(':focus-within') ||
      document.hidden
    ) return;

    this.stopAutoplay();
    this.autoplayTimer = window.setInterval(() => this.moveCarousel(1), this.autoplayDelay);
  }

  restartAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }

  stopAutoplay() {
    if (!this.autoplayTimer) return;
    window.clearInterval(this.autoplayTimer);
    this.autoplayTimer = null;
  }

  disconnectedCallback() {
    this.stopAutoplay();
    this.headingObserver?.disconnect();
    this.galleryObserver?.disconnect();
    this.cardObserver?.disconnect();
    this.carouselResizeObserver?.disconnect();
    this.previousButton?.removeEventListener('click', this.handlePrevious);
    this.nextButton?.removeEventListener('click', this.handleNext);
    this.removeEventListener('mouseenter', this.handlePointerEnter);
    this.removeEventListener('mouseleave', this.handlePointerLeave);
    this.viewport?.removeEventListener('pointerdown', this.handlePointerDown);
    this.viewport?.removeEventListener('pointerup', this.handlePointerUp);
    this.viewport?.removeEventListener('pointercancel', this.handlePointerUp);
    this.removeEventListener('focusin', this.handleFocusIn);
    this.removeEventListener('focusout', this.handleFocusOut);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }
}

if (!customElements.get('tooto-about-collectors')) {
  customElements.define('tooto-about-collectors', TootoAboutCollectors);
}
